/**
 * Clôturer un événement : traiter les leads un par un.
 *
 * L'ordre de l'écran suit l'ordre du travail : qui j'ai rencontré → ce que je
 * lui propose → l'e-mail → envoyé. Un compteur en tête, parce qu'une liste de
 * vingt prospects se termine quand on voit qu'elle avance.
 *
 * L'IA apprend de vos corrections : la proposition d'origine est gardée à côté
 * de votre version, et les dernières réécritures sont montrées au modèle la
 * fois suivante. C'est dit à l'écran — un apprentissage invisible serait une
 * promesse invérifiable.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Mail, Sparkles, Wand2 } from 'lucide-react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, PageTitle, SectionLabel } from '../../admin/ui.tsx';

type Offre = { nom: string; detail: string | null };
type EmailProspect = {
  id: string;
  offres: string[];
  objet_propose: string | null;
  corps_propose: string | null;
  objet: string | null;
  corps: string | null;
  corrige: boolean;
  statut: 'brouillon' | 'valide' | 'envoye' | 'ignore';
  envoye_le: string | null;
};
type Lead = {
  note_id: string;
  nom: string | null;
  entreprise: string | null;
  fonction: string | null;
  email_contact: string | null;
  notes: string | null;
  joignable: boolean;
  email: EmailProspect | null;
  offres_suggerees: string[];
};
type Donnees = {
  ia_disponible: boolean;
  mes_offres: Offre[];
  leads: Lead[];
  avancement: { leads: number; traites: number; envoyes: number };
};

export function Relance() {
  const { evenementId = '' } = useParams();
  const [d, setD] = useState<Donnees | null>(null);

  const charger = useCallback(async () => {
    const r = await appelerApi<Donnees>(`/api/relance/evenements/${evenementId}`);
    setD(r.donnees);
  }, [evenementId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (!d) return <p className="text-[14px] text-fumee">Chargement…</p>;

  const reste = d.avancement.leads - d.avancement.traites;

  return (
    <>
      <Link to="/compte/carnet" className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-fumee hover:text-blanc">
        <ArrowLeft size={14} strokeWidth={1.5} /> Mon carnet
      </Link>

      <PageTitle
        title={
          <>
            Traiter mes <em className="text-match-gradient font-serif italic font-medium">leads</em>
          </>
        }
        subtitle="Pour chacun : ce que vous lui proposez, l’e-mail que l’IA rédige, votre correction. Chaque correction apprend à la suivante."
      />

      <Card className="mb-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-6">
            <Compteur valeur={d.avancement.leads} libelle="rencontres" />
            <Compteur valeur={d.avancement.traites} libelle="traitées" />
            <Compteur valeur={d.avancement.envoyes} libelle="envoyées" />
          </div>
          <p className="max-w-[320px] text-[13px] leading-relaxed text-fumee">
            {reste > 0
              ? `Il en reste ${reste}. Les e-mails envoyés dans les 48 heures obtiennent le plus de réponses.`
              : 'Tout est traité. Beau travail.'}
          </p>
        </div>
      </Card>

      {!d.ia_disponible && (
        <p className="mb-5 text-[13px] leading-relaxed text-fumee">
          L’IA n’est pas encore configurée sur la plateforme : les brouillons proposés restent simples, à
          retravailler. Tout le reste fonctionne.
        </p>
      )}

      {d.mes_offres.length === 0 && (
        <Card className="mb-5">
          <p className="max-w-[560px] text-[14px] leading-relaxed text-fumee">
            Vous n’avez pas encore décrit ce que vous proposez. Remplissez « Ce que je propose » dans{' '}
            <Link to="/compte/entreprise" className="text-blanc underline">Moi et mon entreprise</Link> : c’est ce
            catalogue qui sert à choisir quoi proposer à chaque prospect.
          </p>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        {d.leads.length === 0 && (
          <Card>
            <p className="text-[14px] leading-relaxed text-fumee">
              Aucune rencontre notée pour cet événement. Les notes prises dans votre carnet apparaissent ici.
            </p>
          </Card>
        )}
        {d.leads.map((l) => (
          <CarteLead key={l.note_id} lead={l} offres={d.mes_offres} onChange={charger} />
        ))}
      </div>
    </>
  );
}

function Compteur({ valeur, libelle }: { valeur: number; libelle: string }) {
  return (
    <span>
      <span className="block font-serif text-[28px] leading-none text-blanc">{valeur}</span>
      <span className="label-caps text-fumee">{libelle}</span>
    </span>
  );
}

function CarteLead({ lead, offres, onChange }: { lead: Lead; offres: Offre[]; onChange: () => Promise<void> }) {
  const [ouvert, setOuvert] = useState(false);
  const [choisies, setChoisies] = useState<string[]>(lead.email?.offres.length ? lead.email.offres : lead.offres_suggerees);
  const [objet, setObjet] = useState(lead.email?.objet ?? '');
  const [corps, setCorps] = useState(lead.email?.corps ?? '');
  const [emailId, setEmailId] = useState<string | null>(lead.email?.id ?? null);
  const [propose, setPropose] = useState<{ objet: string | null; corps: string | null } | null>(
    lead.email ? { objet: lead.email.objet_propose, corps: lead.email.corps_propose } : null,
  );
  const [statut, setStatut] = useState(lead.email?.statut ?? 'brouillon');
  const [travail, setTravail] = useState<'repos' | 'generation' | 'envoi'>('repos');
  const [message, setMessage] = useState<string | null>(null);

  const titre = lead.nom?.trim() || lead.entreprise?.trim() || 'Rencontre sans nom';

  const generer = async () => {
    setTravail('generation');
    setMessage(null);
    const r = await envoyer<{ email: EmailProspect; genere: boolean; message?: string }>(
      `/api/relance/notes/${lead.note_id}/generer`,
      { offres: choisies },
    );
    setTravail('repos');
    if (!r.donnees) {
      setMessage(r.erreur ?? 'La génération a échoué.');
      return;
    }
    const e = r.donnees.email;
    setEmailId(e.id);
    setObjet(e.objet ?? '');
    setCorps(e.corps ?? '');
    setPropose({ objet: e.objet_propose, corps: e.corps_propose });
    setStatut(e.statut);
    setOuvert(true);
    if (r.donnees.message) setMessage(r.donnees.message);
  };

  const enregistrer = async (nouveauStatut?: 'brouillon' | 'valide' | 'ignore') => {
    if (!emailId) return;
    const r = await envoyer<EmailProspect>(`/api/relance/emails/${emailId}`, { objet, corps, offres: choisies, statut: nouveauStatut }, 'PUT');
    if (r.donnees) {
      setStatut(r.donnees.statut);
      if (r.donnees.corrige) setMessage('Votre version est gardée comme exemple : les prochains e-mails suivront ce style.');
      await onChange();
    }
  };

  const expedier = async () => {
    if (!emailId) return;
    setTravail('envoi');
    setMessage(null);
    await enregistrer();
    const r = await envoyer<{ envoye: boolean; message: string }>(`/api/relance/emails/${emailId}/envoyer`, {});
    setTravail('repos');
    setMessage(r.donnees?.message ?? r.erreur ?? 'Envoi impossible.');
    if (r.donnees?.envoye) {
      setStatut('envoye');
      await onChange();
    }
  };

  const modifie = propose && (propose.corps ?? '') !== corps;

  return (
    <Card className={ouvert ? 'border border-graphite' : ''}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button onClick={() => setOuvert(!ouvert)} className="min-w-0 flex-1 text-left">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[16px] text-blanc">{titre}</span>
            {lead.entreprise && lead.nom && <span className="text-[13px] text-fumee">{lead.entreprise}</span>}
            {statut === 'envoye' && <Badge filled>Envoyé</Badge>}
            {statut === 'valide' && <Badge>Prêt</Badge>}
            {statut === 'ignore' && <Badge>Écarté</Badge>}
            {!lead.joignable && <Badge>Pas d’e-mail</Badge>}
          </span>
          {lead.notes?.trim() && (
            <span className="mt-1 block truncate text-[12px] text-fumee">{lead.notes.trim().slice(0, 100)}</span>
          )}
        </button>
        {statut !== 'envoye' && (
          <Btn variant={emailId ? 'ghost' : 'primary'} onClick={() => void generer()} disabled={travail === 'generation'}>
            <Wand2 size={13} strokeWidth={1.5} className="mr-1.5 inline" />
            {travail === 'generation' ? 'Rédaction…' : emailId ? 'Refaire' : 'Rédiger l’e-mail'}
          </Btn>
        )}
      </div>

      {ouvert && (
        <div className="mt-5 border-t border-graphite pt-5">
          <SectionLabel>Ce que je lui propose</SectionLabel>
          {offres.length === 0 ? (
            <p className="mt-2 text-[13px] text-fumee">Aucun service décrit dans votre profil.</p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {offres.map((o) => {
                const actif = choisies.includes(o.nom);
                return (
                  <button
                    key={o.nom}
                    title={o.detail ?? undefined}
                    onClick={() => setChoisies(actif ? choisies.filter((x) => x !== o.nom) : [...choisies, o.nom])}
                    className={`rounded-full border border-graphite px-3 py-1.5 text-[13px] transition-colors ${actif ? 'bg-blanc text-void' : 'text-fumee hover:text-blanc'}`}
                  >
                    {actif && <Check size={11} strokeWidth={2} className="mr-1 inline" />}
                    {o.nom}
                  </button>
                );
              })}
            </div>
          )}

          {emailId && (
            <>
              <div className="mt-5 flex flex-col gap-3">
                <label className="block">
                  <span className="label-caps block text-fumee">Objet</span>
                  <input value={objet} onChange={(e) => setObjet(e.target.value)} className="field-input mt-2 !min-h-[40px] !py-2 !text-sm" />
                </label>
                <label className="block">
                  <span className="label-caps block text-fumee">Message {lead.email_contact ? `· à ${lead.email_contact}` : ''}</span>
                  <textarea rows={10} value={corps} onChange={(e) => setCorps(e.target.value)} className="field-input mt-2 !min-h-0 !py-2 !text-sm" />
                </label>
              </div>

              {modifie && (
                <p className="mt-2 flex items-start gap-1.5 text-[12px] leading-relaxed text-fumee">
                  <Sparkles size={12} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                  Vous avez réécrit la proposition — elle servira d’exemple pour les prochains e-mails.
                </p>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Btn onClick={() => void enregistrer('valide')}>Enregistrer</Btn>
                <Btn
                  variant="primary"
                  onClick={() => void expedier()}
                  disabled={travail === 'envoi' || !lead.joignable || statut === 'envoye'}
                >
                  <Mail size={13} strokeWidth={1.5} className="mr-1.5 inline" />
                  {travail === 'envoi' ? 'Envoi…' : statut === 'envoye' ? 'Déjà envoyé' : 'Envoyer'}
                </Btn>
                {lead.email_contact && (
                  <a
                    href={`mailto:${lead.email_contact}?subject=${encodeURIComponent(objet)}&body=${encodeURIComponent(corps)}`}
                    className="text-[13px] text-fumee hover:text-blanc"
                  >
                    Ouvrir dans ma messagerie
                  </a>
                )}
                {statut !== 'ignore' && (
                  <button onClick={() => void enregistrer('ignore')} className="text-[13px] text-fumee hover:text-blanc">
                    Écarter
                  </button>
                )}
              </div>
            </>
          )}

          {message && <p className="mt-3 text-[13px] leading-relaxed text-blanc">{message}</p>}
        </div>
      )}
    </Card>
  );
}
